import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaInfoCircle } from "react-icons/fa";
import { getApiBase } from "../apiBase";


const InfoTooltip = ({ text }) => (
  <span className="relative inline-block ml-1 group align-middle">
    <span className="cursor-pointer text-gray-500 text-[11px] font-medium border border-gray-400 rounded-full w-4 h-4 inline-flex items-center justify-center">
      i
    </span>
    <div className="absolute left-1/2 -translate-x-1/2 top-6 w-72 px-3 py-2 text-[11px] leading-relaxed text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition pointer-events-none z-20">
      {text}
    </div>
  </span>
);

const ScrapeTooltip = ({ text }) => (
  <span className="relative inline-block ml-1 group align-middle">
    <FaInfoCircle className="cursor-pointer text-gray-500 text-xs" />
    <div className="absolute left-1/2 -translate-x-1/2 top-6 w-80 px-3 py-2 text-[11px] leading-relaxed text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition pointer-events-none z-20">
      {text}
    </div>
  </span>
);

export default function KnowledgeBase() {
  const { tenantId, botId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [toast, setToast] = useState(null);

  const [scrapingCompleted, setScrapingCompleted] = useState(false);
  const [showConfirmScrape, setShowConfirmScrape] = useState(false);
  const [showPagesPopup, setShowPagesPopup] = useState(false);
  const [newDocs, setNewDocs] = useState([]);
  const [botName, setBotName] = useState("");
  const uploadPollRef = useRef(null);





  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchBotName = async () => {
  try {
    const res = await fetch(
      `${getApiBase()}/tenant/${tenantId}/bot/${botId}/details`
    );

    if (!res.ok) throw new Error("Failed to fetch bot details");

    const response = await res.json();

    setBotName(
      response?.data?.bot_name || "Your Bot"
    );
  } catch (err) {
    console.error("Bot name fetch failed:", err);
    setBotName("Your Bot");
  }
};



  const fetchDocuments = async () => {
  try {
    const res = await fetch(
      `${getApiBase()}/knowledge-base?tenant_id=${tenantId}&bot_id=${botId}`
    );
    const data = await res.json();

    const normalized = (data.files || []).map(doc => ({
      ...doc,
      // ✅ always guarantee a usable date
      uploaded_at:
        doc.uploaded_at ||
        doc.created_at ||
        new Date().toISOString()
    }));

    setDocuments(normalized);
  } catch {
    // safety
  }
};




 useEffect(() => {
  if (!tenantId || !botId) return;

  fetchDocuments();
  fetchBotName();
}, [tenantId, botId]);



  const fetchPages = async () => {
    if (!websiteUrl.trim()) {
      showToast("Enter website URL", "error");
      return;
    }

    setLoadingPages(true);

    try {
      const res = await fetch(
        `${getApiBase()}/sitemap-pages?tenant_id=${tenantId}&bot_id=${botId}&url=${encodeURIComponent(
          websiteUrl
        )}`
      );
      const data = await res.json();
      setPages(data.pages || []);
      setSelectedPages([]);
      setShowPagesPopup(true);
    } catch {
      showToast("Failed to fetch pages", "error");
    } finally {
      setLoadingPages(false);
    }
  };

  const scrapeSelected = async () => {
  if (selectedPages.length === 0) {
    showToast("Select at least one page", "error");
    return;
  }

  setScraping(true); // ✅ Spinner ON immediately

  try {
    await fetch(`${getApiBase()}/scrape-selected-pages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_id: tenantId,
        bot_id: botId,
        pages: selectedPages
      })
    });

    showToast("Scraping started...");

    waitForScraping(); // ✅ LIVE backend tracking starts here

    // ✅ DO NOT CLEAR SCRAPE UI (VERY IMPORTANT)
    // ❌ REMOVE THESE LINES:
    // setPages([]);
    // setWebsiteUrl("");
    // setSelectedPages([]);

  } catch {
    showToast("Scraping failed", "error");
    setScraping(false);
  }
};




  const waitForScraping = () => {
  const interval = setInterval(async () => {
    try {
      console.log("Polling knowledge-base...");

      const res = await fetch(
        `${getApiBase()}/knowledge-base?tenant_id=${tenantId}&bot_id=${botId}`
      );

      const data = await res.json();
      const files = data.files || [];

      setDocuments(files); 

      const stillProcessing = files.some(
        (doc) => doc.status === "processing"
      );

      
      if (!stillProcessing && files.length > 0) {
        clearInterval(interval);
        setScraping(false);
        setScrapingCompleted(true);
        setShowPagesPopup(false);   
        setPages([]);               
        setSelectedPages([]);       
        setWebsiteUrl("");          
        showToast("Scraping completed");
      }

    } catch (err) {
      console.error("Polling failed", err);
    }
  }, 10000); // ✅ EVERY 10 SECONDS (SAFE & REAL-TIME)
};

const toggleSelectAllPopup = () => {
  if (selectedPages.length === pages.length) {
    setSelectedPages([]);
  } else {
    setSelectedPages(pages);
  }
};


  const handleUpload = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  setUploading(true); // spinner ON

  try {
    const fd = new FormData();
    fd.append("file", file);

    const res = await fetch(
      `${getApiBase()}/documents/ingest?tenant_id=${tenantId}&bot_id=${botId}`,
      { method: "POST", body: fd }
    );

    const data = await res.json();

    // ❌ Backend rejected upload (size > 10MB etc.)
    if (!res.ok) {
      showToast(data.detail || "Upload failed", "error");
      setUploading(false); // ✅ STOP spinner
      return;
    }

    showToast("Upload started");

    // 🔁 START polling knowledge-base
    uploadPollRef.current = setInterval(async () => {
      const res = await fetch(
        `${getApiBase()}/knowledge-base?tenant_id=${tenantId}&bot_id=${botId}`
      );
      const data = await res.json();
      const files = data.files || [];

      setDocuments(files);

      // ✅ Stop spinner when ALL files are active
      const stillProcessing = files.some(
        (doc) => doc.status === "processing"
      );

      if (!stillProcessing && files.length > 0) {
        clearInterval(uploadPollRef.current);
        uploadPollRef.current = null;
        setUploading(false); // ✅ STOP spinner
        showToast("Upload completed");
      }
    }, 2000);

  } catch (err) {
    showToast("Upload failed", "error");
    setUploading(false);
  }
};

  const toggleSelectAll = () => {
    if (selectedPages.length === pages.length) {
      setSelectedPages([]);
    } else {
      setSelectedPages(pages);
    }
  };

  return (
    <div className="min-h-screen bg-white px-8 py-10">
      {toast && (
        <div
          className={`fixed top-5 right-5 px-4 py-2 rounded-md text-sm text-white shadow ${
            toast.type === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <p className="text-sm text-gray-700 font-semibold">
            Step <span className="text-gray-900">2</span> of 3
          </p>
          <div className="h-1.5 w-44 bg-gray-300 rounded-full overflow-hidden">
            <div className="h-full w-2/3 bg-[#5B3A60]" />
          </div>
        </div>

        <div className="border-b-2 pb-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            {botName}<ScrapeTooltip text="This is the display name of your bot. It helps identify and manage the assistant across the platform." />
          </h2>

          <h1 className="text-lg font-semibold text-gray-900">
            Knowledge Base
          </h1>

          <p className="text-sm text-gray-600 mt-1">
            Upload documents or scrape website pages.
          </p>

        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
          <div
          onClick={() => !uploading && fileInputRef.current.click()}
          className="relative border-2 border-dotted border-gray-500 rounded-lg px-6 py-10 cursor-pointer hover:bg-gray-50 transition"
        >
          <p className="font-bold text-gray-900 text-sm">Upload Documents</p>
          <p className="text-xs text-gray-600 mt-1">PDF or TXT supported</p>

          <p className="mt-4 text-sm text-[#5B3A60] font-semibold">
            {uploading ? "Uploading..." : "Click to upload"}
          </p>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.txt"
            onChange={handleUpload}
          />

          {uploading && (
            <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center rounded-lg">
              <div className="w-10 h-10 border-4 border-[#5B3A60] border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-sm font-semibold text-[#5B3A60]">Uploading...</p>
            </div>
          )}
        </div>

          <div className="border-2 border-dotted border-gray-500 rounded-lg px-6 py-10">
          <p className="font-bold text-gray-900 mb-4 text-sm">
            Scrape Website<ScrapeTooltip text="Enter URL to search the entire sitemap and scrape selected pages to the knowledge base. Please note: Different websites can have distinct site policy and designs." />
          </p>
  

  <input
    value={websiteUrl}
    onChange={(e) => setWebsiteUrl(e.target.value)}
    placeholder="https://example.com"
    className="w-full border-2 px-3 py-2 rounded-md text-sm mb-4 focus:ring-1 focus:ring-[#5B3A60] outline-none"
  />

  <button
    onClick={fetchPages}
    className="w-full border-2 border-[#5B3A60] text-[#5B3A60] py-2 rounded-md text-sm font-bold"
  >
    {loadingPages ? "Fetching..." : "Fetch Pages"}
  </button>
</div>


{showPagesPopup && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white w-[520px] max-h-[80vh] rounded-lg p-6 shadow-lg relative">

      <h2 className="text-lg font-bold mb-2 text-gray-900">
        Select Pages to Scrape
      </h2>

      <div className="flex justify-between items-center mb-3">
  <p className="text-xs text-gray-500">
    Estimated time: 2-3 minutes
  </p>

  <button
    onClick={toggleSelectAllPopup}
    className="text-xs font-bold text-[#5B3A60]"
  >
    {selectedPages.length === pages.length ? "Clear All" : "Select All"}
  </button>
</div>


      <div className="max-h-60 overflow-y-auto border rounded-md p-3 mb-4 space-y-3">
        {pages.map((url) => (
          <label
            key={url}
            className="flex items-center gap-3 text-sm font-medium border-b pb-2 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedPages.includes(url)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedPages((prev) => [...prev, url]);
                } else {
                  setSelectedPages((prev) =>
                    prev.filter((u) => u !== url)
                  );
                }
              }}
            />
            <span className="truncate">{url}</span>
          </label>
        ))}
      </div>

      {/* ✅ SPINNER OVERLAY */}
      {scraping && (
        <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-10 rounded-lg">
          <span className="loader w-12 h-12 animate-spin mb-3"></span>
          <p className="text-sm font-bold text-[#5B3A60]">
            Scraping in progress...
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Please wait, this may take a few minutes
          </p>
        </div>
      )}

      <div className="flex justify-between items-center mt-5">

        <span className="text-xs font-semibold text-gray-600">
          {selectedPages.length} Selected
        </span>

        <div className="flex gap-3">

          <button
            onClick={() => setShowPagesPopup(false)}
            disabled={scraping}
            className="px-4 py-2 text-sm font-bold border rounded-md text-gray-700"
          >
            Cancel
          </button>

          <button
            onClick={scrapeSelected}
            disabled={scraping}
            className="px-5 py-2 text-sm font-bold rounded-md bg-[#5B3A60] text-white"
          >
            {scraping ? "Scraping..." : "Scrape Selected"}
          </button>

        </div>
      </div>
    </div>
  </div>
)}


        </div>

       

        <div className="border-2 border-gray-400 rounded-lg mt-10">
          <div className="border-b-2 px-4 py-3 bg-gray-100 font-bold text-sm text-gray-800">
            Uploaded Items
          </div>

          {documents.length === 0 ? (
            <div className="py-10 text-center text-gray-500 text-sm">
              No documents yet
            </div>
          ) : (
            <div className="divide-y">
              {documents.map((doc) => (
                <div
                  key={doc.file_name}
                  className="flex items-center justify-between px-4 py-4 hover:bg-gray-50"
                >
                  <div className="flex flex-col w-[55%]">
                    <span className="text-sm font-bold text-gray-900 break-all">
  {doc.file_name}

  {newDocs.includes(doc.file_name) && (
    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">
      NEW
    </span>
  )}
</span>

                    <span className="text-xs font-semibold text-gray-600 uppercase">
                      {doc.file_type}
                    </span>
                  </div>

                  <div className="w-[20%] flex justify-center">
                    {doc.status === "processing" ? (
                      <span className="px-3 py-1 text-xs font-bold rounded-full bg-yellow-100 text-yellow-800">
                        Processing
                      </span>
                    ) : (
                      <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </div>

                  <div className="w-[25%] text-right text-xs font-semibold text-gray-600">
                    {new Date(doc.uploaded_at || doc.created_at).toLocaleString()}
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between mt-10 border-t-2 pt-5">
  <button
    onClick={() =>
      navigate(`/generate-prompts/${tenantId}/${botId}`)
    }
    className="px-8 py-2.5 rounded-md text-sm font-bold bg-[#5B3A60] text-white hover:opacity-90"
  >
    Skip
  </button>

  <button
    onClick={() =>
      navigate(`/generate-prompts/${tenantId}/${botId}`)
    }
    className="px-8 py-2.5 rounded-md text-sm font-bold bg-[#5B3A60] text-white hover:opacity-90"
  >
    Next
  </button>
        </div>
      </div>
    </div>
  );
}
